"use client";

import { useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getUser, USER_GET } from "@/api";
import { Role, User } from "@/types";
import { routes } from "@/constants";
import { FeatureFlag, FeatureFlagName } from "@/types/feature-flag";
import { getFeatureFlags, FEATURE_FLAGS_GET } from "@/api/client";
import { useRouter } from "next/navigation";
import { AUTH_SESSION_DATA, getSession, sessionLogout } from "@/api/auth";
import { Member } from "stytch";
import { AxiosError } from "axios";
import { useAnalytics } from "@/lib";
import { canNavigateAway } from "@/lib/navigationGuard";

export const useSession = () => {
  const queryClient = useQueryClient();
  const {
    data: sessionData,
    isFetched,
    error,
  } = useQuery<Member | null>({
    queryKey: [AUTH_SESSION_DATA],
    queryFn: getSession,
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    retryDelay: 300,
    retry: 1,
    refetchInterval: (query) => {
      // Only refetch if we have a session (don't poll when logged out)
      return query.state.data ? 1000 * 60 * 5 : false; // Check session validity every 5 minutes
    },
  });
  const { reset, identify } = useAnalytics();
  const userId: string | undefined = sessionData?.trusted_metadata?.focalid;

  const router = useRouter();

  const { data: userData } = useQuery<User | undefined>({
    queryKey: [USER_GET, userId],
    queryFn: () => getUser(userId),
    enabled: !!userId,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry on authentication errors
      if (failureCount >= 1) {
        router.push(routes.auth.authenticate);
        return false;
      }
      return (
        failureCount < 2 && (error as AxiosError)?.response?.status !== 401
      );
    },
    retryDelay: 300,
  });

  const { data: featureFlagsData, isFetched: isFeatureFlagFetched } = useQuery<
    Record<string, FeatureFlag>
  >({
    queryKey: [FEATURE_FLAGS_GET],
    queryFn: () => getFeatureFlags(),
    enabled: !!userId,
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 300,
  });

  const userName = useMemo(() => {
    if (!userData) return "";
    return `${userData?.firstname || ""} ${userData?.lastname || ""}`;
  }, [userData]);

  const getFeatureFlag = (featureName: FeatureFlagName): FeatureFlag => {
    return (
      featureFlagsData?.[featureName] || { value: "control", config: null }
    );
  };

  const clearLocalSession = useCallback(() => {
    queryClient.clear();
    localStorage.clear();
    sessionStorage.clear();
    reset();
  }, [queryClient, reset]);

  const handleLogout = useCallback(async () => {
    // Checked here rather than at the router push below: by then the session
    // is already revoked, so a guarded page would strand the user on it with
    // no way to save their work.
    if (!canNavigateAway()) return;

    clearLocalSession();
    try {
      await sessionLogout();
    } catch (error) {
      const errorStatus = (error as AxiosError).response?.status;

      // 401/404 means there was no session left to revoke, which is the state
      // we were trying to reach. Only a real failure is worth reporting.
      if (errorStatus !== 401 && errorStatus !== 404) {
        console.error("Error logging out:", error);
      }
    }
    router.push(routes.auth.authenticate);
  }, [clearLocalSession, router]);

  useEffect(() => {
    if (error) {
      const errorStatus = (error as AxiosError).response?.status;

      // Session validation already failed, so the session is gone server-side.
      // Clear locally and redirect — calling logout here would just 401 again
      // and surface a phantom "Error logging out" on every anonymous page load.
      if (errorStatus === 401 || errorStatus === 404) {
        clearLocalSession();
        router.push(routes.auth.authenticate);
      }
    }
  }, [error, clearLocalSession, router]);

  // Extract region from session data (added by our session API route)
  const region = (sessionData as Member & { region?: string })?.region || "unknown";

  useEffect(() => {
    if (userData) {
      identify(userData.id, {
        $user_id: userData.id, // default posthog user identifier
        focalUserId: userData.id,
        email: userData.email, // Pass email for internal user filtering
        focalFirmId: userData.firmId,
        crmUserId: userData.crmUserId || "not-set",
        country_code: region,
      });
    }
  }, [userData?.id, region]);

  const userRoles: Role[] = useMemo(() => {
    return sessionData?.roles?.map((role) => role.role_id as Role) || [];
  }, [sessionData?.roles]);

  return {
    session: sessionData,
    userName,
    userId: userData?.id || userId,
    isReady: isFetched,
    user: userData,
    getFeatureFlag,
    handleLogout,
    isFeatureFlagFetched,
    userRoles,
    region,
  };
};
